import { defineStore } from 'pinia'
import {
    ApiRes,
    FrontendBankAccountResource,
    getAccounts,
    getBankAccountsForNaturalProfile,
    getKeycloakCredentialsForNaturalProfile,
    getKycDocumentsForNaturalProfile,
    getReferralCodesForNaturalProfile,
    KycDocument,
    KycDocumentSaveRequestBody,
    postGenerateNewReferralCode,
    postKycDocument,
    putNaturalProfile,
} from '../api'
import { NotificationType, notify, useNotificationState } from '../useNotifications'
import { i18n } from '../../localization'
import { parseDate, promiseHandler } from '../helpers'
import { usePersonalAccountFormState } from './useAccountFormPersonal'
import { isRequiredNaturalProfileDocuments, requiredNaturalProfileAttributesForInvestment } from '../../validation'
import { reactive } from 'vue'
import { format } from 'date-fns'
import {
    ComplianceProfile,
    EcspStatus,
    FollowMarketOption,
    Gender,
    InvestmentFrequencyOption,
    InvestmentHorizonOption,
    InvestmentKnowledgeOption,
    RiskToleranceOption,
    Voucher,
} from '../../typings'
import {
    getLockableNaturalProfileAttributes,
    getLockableNaturalProfileRelations,
    getPlatformSettings,
} from '../usePlatformSettings'
import { CompanyOwnership } from '../../components/wizard/ESAC/legal/form/forms'
import { App } from '../../typings/api-enums'
import { isEsacPersonalComplianceProfileLocked } from '../../components/account/personal/complianceProfile/esacComplianceProfile'

class SourceOfFunds {
    dividends_or_profits = false
    lottery_gambling = false
    divorce = false
    savings = false
    maturity_or_surrender = false
    gift = false
    inheritance = false
    insurance_claims = false
    sale_of_property = false
    loan = false
    other = ''
}

export class FinancialConditions {
    financial_assets = null
    annual_operating_result = null
    dept_capital = null
    industry = ''
    properties = false
    source_of_funds = new SourceOfFunds()
}

export class InvestorClassification {
    expert_investor_treatment: App.Enums.ComplianceProfile.YesNoOption = null
    self_disclosure: any | null = null
}

export class Experience {
    no_answer = false
    financial_education: App.Enums.ComplianceProfile.ECSP.FinancialEducation = null
    bonds_knowledge: App.Enums.ComplianceProfile.ECSP.YesNoDenyOption = null
    bonds_investment_frequency: App.Enums.ComplianceProfile.ECSP.InvestmentFrequencyOption = null
    stocks_knowledge: App.Enums.ComplianceProfile.ECSP.YesNoDenyOption = null
    stocks_investment_frequency: App.Enums.ComplianceProfile.ECSP.InvestmentFrequencyOption = null
    convertible_bonds_knowledge: App.Enums.ComplianceProfile.ECSP.YesNoDenyOption = null
    convertible_bonds_investment_frequency: App.Enums.ComplianceProfile.ECSP.InvestmentFrequencyOption = null
}

export class Risk {
    no_answer = false
    startup_investing: App.Enums.ComplianceProfile.ECSP.StartupInvestingAnswerOption = null
    investments_outside_stock_market: App.Enums.ComplianceProfile.ECSP.InvestmentsOutsideStockMarketAnswerOption = null
    subordinated_financial_instruments: App.Enums.ComplianceProfile.ECSP.SubordinatedFinancialInstrumentsAnswerOption =
        null
    profit_participation_certificate: App.Enums.ComplianceProfile.ECSP.ProfitParticipationCertificateAnswerOption = null
}

export class InvestmentGoals {
    no_answer = false
    sustainability: App.Enums.ComplianceProfile.ECSP.SustainabilityAnswerOption = null
    investment_goal: App.Enums.ComplianceProfile.ECSP.InvestmentGoalAnswerOption = null
    investment_horizon: App.Enums.ComplianceProfile.ECSP.InvestmentHorizonAnswerOption = null
}

export class FinancialSituation {
    annual_net_income: number = null
    liquid_funds: number = null
    annual_financial_commitments: number = null
    net_worth: number = null
    loss_bearing_capacity: number = null
}

export class ExperienceAndKnowledge {
    stocks_equity_funds_knowledge: InvestmentKnowledgeOption = null
    mixed_funds_etfs_knowledge: InvestmentKnowledgeOption = null
    bonds_bond_funds_knowledge: InvestmentKnowledgeOption = null
    closed_ended_funds_knowledge: InvestmentKnowledgeOption = null
    options_other_derivatives_knowledge: InvestmentKnowledgeOption = null
    venture_capital_knowledge: InvestmentKnowledgeOption = null
    other_shares_loans_in_private_companies_knowledge: InvestmentKnowledgeOption = null
    other_alternative_investments_knowledge: InvestmentKnowledgeOption = null
    stocks_equity_funds_experience = null
    mixed_funds_etfs_experience = null
    bonds_bond_funds_experience = null
    closed_ended_funds_experience = null
    options_other_derivatives_experience = null
    venture_capital_experience = null
    other_shares_loans_in_private_companies_experience = null
    other_alternative_investments_experience = null
    follow_market_prices_business_financial_news: FollowMarketOption = null
}

export class RiskTolerance {
    risk_tolerance: RiskToleranceOption = null
}

class FatcaDeclaration {
    us_citizen_or_resident: string = null
}

export class LegalFatcaDeclaration {
    us_entity: string = null
    financial_institutions_under_fatca: string = null
    non_financial_entity_reason: string = null
    financial_institution_category: string = null
    no_giin_reason: string = null
    provide_giin: string = null
    giin: string = null
    giin_sponsor_name: string = null
    giin_sponsor: string = null
}

export class TaxResidency {
    country = ''
    tin = ''
    cant_obtain_tin = false
    no_tin_reason = ''
}

class TaxSituation {
    capital_gains = false
    foreign_assets = false
}

class TaxSituationCategory {
    tax_situation = new TaxSituation()
}

class Acknowledgements {
    currency_change_risk = false
    own_account = false
    anti_money_laundering = false
    self_certification_form = false
    tax_data_exchange_agreement = false
    report_changes = false
    complete_and_correct_information = false
}

class InvestmentObjectives {
    other_investment_target = ''
    security_coverage = false
    retention_of_assets = false
    distribution_of_assets = false
    capital_gain = false
}

export class InvestmentTargets {
    investment_objectives = new InvestmentObjectives()
    investment_horizon: InvestmentHorizonOption = null
    investment_frequency: InvestmentFrequencyOption = null
}

export class EsacComplianceProfile {
    financial_conditions = new FinancialConditions()
    experience_and_knowledge = new ExperienceAndKnowledge()
    risk_tolerance = new RiskTolerance()
    fatca_declaration = new FatcaDeclaration()
    company_ownership = new CompanyOwnership()
    tax_situation = new TaxSituationCategory()
    acknowledgements = new Acknowledgements()
    investment_targets = new InvestmentTargets()
}

export class EcspComplianceProfile {
    investor_classification = new InvestorClassification()
    experience = new Experience()
    risk = new Risk()
    investment_goals = new InvestmentGoals()
    financial_situation = new FinancialSituation()
}

export class ComplianceProfiles {
    esac? = new EsacComplianceProfile()
    ecsp? = new EcspComplianceProfile()
}

export class NaturalProfileMinimal {
    natural_profile_uid = ''
    postal_address = ''
    postal_address_postal_code = ''
    postal_address_city = ''
    postal_address_state = ''
    postal_address_country = ''
    compliance_profiles: ComplianceProfiles = null
    // Financial Conditions
    tax_residencies: TaxResidency[] = []
    address = ''
    bic = ''
    city = ''
    country = ''
    country_of_birth = ''
    city_of_birth = ''
    country_name = ''
    date_of_birth = null
    email = ''
    email_pending = ''
    first_name = ''
    gender: Gender = null
    iban = ''
    last_name = ''
    mobile_number = ''
    nationality = ''
    nationality_name = '' // TODO: what is this?
    pep = false
    pep_position = ''
    pep_past = false
    pep_past_position = ''
    phone_number = ''
    postal_code = ''
    social_security_number = ''
    state = ''
    tax_number = ''
    highest_educational_attainment = ''
    current_occupation = ''
    experience_share = ''
    experience_bond = ''
    experience_participation_paper = ''
    experience_crypto_currency = ''
    experience_unsecured_junior_debt = ''
    knowledge_share = false
    knowledge_bond = false
    knowledge_participation_paper = false
    knowledge_crypto_currency = false
    knowledge_unsecured_junior_debt = false
    average_investment_transactions_per_year = ''
    average_investment_transaction_amount = ''
    experience_without_advice = ''
    experience_with_investment_advice = ''
    experience_with_asset_management = ''
    ecsp_status: typeof EcspStatus = null
    default_language = ''
    membership_number: string | null
    membership_document = []
    membership_document_description: string | null
    external_kyc_done = false
    depot_owner = ''
    depot_number = ''
    depot_bank = ''
    depot_bic = ''
}

class NaturalProfileClass extends NaturalProfileMinimal {
    depot_owner = ''
    depot_number = ''
    depot_bank = ''
    depot_bic = ''
    kyc_address = false
    kyc_bank = false
    kyc_id = false
    kyc_tax = false
    kyc_documents = [] as KycDocument[]
    kyc_verified = false
    ecsp_status: typeof EcspStatus = null
    platform_name = ''
    platform_kyc_provider = ''
    portfolio_sum = []
    is_locked = false
    signatures = [] as Signature[]
    // voucher = null as Voucher
    vouchers = [] as Voucher[]
}

export type NaturalProfileRequestBody = NaturalProfileMinimal
export type NaturalProfile = NaturalProfileClass

export interface Signature {
    link: string
    type: string
    document_type: string
    signed_at: string
    cancelled_at: string
    unsigned_document_link: string
}

function applyComplianceProfileDefaults(naturalProfile: NaturalProfile): NaturalProfile {
    const naturalComplianceProfileDefaults: Record<ComplianceProfile, any> = {
        esac: new EsacComplianceProfile(),
        ecsp: new EcspComplianceProfile(),
    }

    Object.keys(naturalComplianceProfileDefaults).forEach((complianceProfile) => {
        if (naturalProfile.compliance_profiles && getPlatformSettings().compliance_profile === complianceProfile) {
            naturalProfile.compliance_profiles = {
                [complianceProfile]: Object.assign(
                    naturalComplianceProfileDefaults[complianceProfile],
                    naturalProfile.compliance_profiles[complianceProfile]
                ),
            }
        }
    })

    return naturalProfile
}

function mapLegacyNaturalProfileData(person: NaturalProfile): NaturalProfile {
    if (person.date_of_birth) {
        person.date_of_birth = format(parseDate(person.date_of_birth), 'yyyy-MM-dd')
    }

    return person
}

// useStore could be anything like useUser, useCart
// the first argument is a unique id of the store across your application
export const usePersonalAccountDataState = defineStore('personal-account-data', {
    state: () => {
        return {
            naturalProfile: new NaturalProfileClass(),
            hasLegalProfile: false,
            legalProfileKyc: false,
            loading: false,
            loaded: false,
            saving: false,
            accountId: null,
            kycDocuments: reactive([]),
            referralCodes: reactive([]),
            keycloakCredentials: reactive([]),
            kycDocumentsLoading: false,
            referralCodesLoading: false,
            credentialsLoading: false,
            bankAccountsLoading: false,
            bankAccounts: [] as FrontendBankAccountResource[],
        }
    },

    actions: {
        async fetchAccountData() {
            this.loading = true
            const response = await getAccounts()
            let naturalProfileStoreData = mapLegacyNaturalProfileData(response.data.natural_profile)
            naturalProfileStoreData = applyComplianceProfileDefaults(naturalProfileStoreData)
            this.$patch({
                naturalProfile: naturalProfileStoreData,
                accountId: response.data.accounts.natural[0].account_uid,
                hasLegalProfile: !!response.data.legal_profile,
                legalProfileKyc: !!response.data.legal_profile?.kyc_legal,
            })
            this.loading = false
            this.loaded = true
        },
        async fetchKycDocuments() {
            this.kycDocumentsLoading = true
            const response = await getKycDocumentsForNaturalProfile(this.naturalProfile.natural_profile_uid)
            this.kycDocumentsLoading = false

            this.$patch({ kycDocuments: response.data })
        },
        async fetchBankAccounts() {
            this.bankAccountsLoading = true
            const { data } = await getBankAccountsForNaturalProfile(this.naturalProfile.natural_profile_uid)
            this.bankAccountsLoading = false

            this.$patch({ bankAccounts: data })
        },
        async fetchReferralCodes() {
            this.referralCodesLoading = true
            const response = await getReferralCodesForNaturalProfile(this.naturalProfile.natural_profile_uid)
            this.referralCodesLoading = false

            this.$patch({ referralCodes: response.data })
        },
        async fetchCredentials() {
            this.credentialsLoading = true
            getKeycloakCredentialsForNaturalProfile(this.naturalProfile.natural_profile_uid)
                .then((response) => {
                    this.$patch({ keycloakCredentials: response.data })
                })
                .finally(() => {
                    this.credentialsLoading = false
                })
        },
        async saveAccountData(): Promise<ApiRes> {
            return new Promise(async (resolve, reject) => {
                this.saving = true

                const requestData: NaturalProfileRequestBody = new NaturalProfileMinimal()
                Object.keys(new NaturalProfileMinimal()).forEach((item) => {
                    requestData[item] = this.naturalProfile[item]
                })

                const naturalProfileId = this.naturalProfile.natural_profile_uid

                const [data, error] = await promiseHandler(putNaturalProfile(naturalProfileId, requestData))

                if (error) {
                    reject(error)
                } else {
                    const notifications = useNotificationState()

                    notifications.addNotification(
                        'Success' as NotificationType,
                        i18n.global.t('account.saved_successfully_title'),
                        i18n.global.t('account.saved_successfully_message')
                    )

                    const accountFormState = usePersonalAccountFormState()

                    accountFormState.currentForm.resetForm({
                        values: accountFormState.currentForm.values,
                    })

                    resolve(data)
                }
                this.saving = false
            })
        },
        saveKycDocument(kycDocument: KycDocument) {
            const reqBody: KycDocumentSaveRequestBody = {
                ...kycDocument,
                natural_profile_uid: this.naturalProfile.natural_profile_uid,
            }

            return new Promise((resolve, reject) => {
                postKycDocument(reqBody)
                    .then((data) => {
                        this.kycDocuments.push(data.data)
                        notify(
                            'Success' as NotificationType,
                            i18n.global.t('account.saved_successfully_title'),
                            i18n.global.t('account.saved_kyc_successfully_message')
                        )
                        resolve(data)
                    })
                    .catch(reject)
            })
        },
        generateNewReferralCode() {
            return new Promise((resolve, reject) => {
                postGenerateNewReferralCode(this.naturalProfile.natural_profile_uid)
                    .then((data) => {
                        this.referralCodes.push(data.data)
                        notify(
                            'Success' as NotificationType,
                            i18n.global.t('success'),
                            i18n.global.t('referral_program.generated_referral_code_successfully_message')
                        )
                        resolve(data)
                    })
                    .catch(reject)
            })
        },
    },
    getters: {
        hasAllRequiredNaturalProfileAttributesForInvestment(): boolean {
            return (
                requiredNaturalProfileAttributesForInvestment().filter((attribute) => {
                    return !this.naturalProfile[attribute]
                }).length === 0
            )
        },
        isVerified(): boolean {
            return !(
                (getPlatformSettings().require_kyc_id_for_investments && !this.naturalProfile.kyc_id) ||
                (getPlatformSettings().require_kyc_address_for_investments && !this.naturalProfile.kyc_address) ||
                (getPlatformSettings().require_kyc_tax_for_investments && !this.naturalProfile.kyc_tax)
            )
        },
    },
})

function getAttributesLockedByPlatformSetting() {
    return [...new Set([...window.condaLockedAttributes, ...window.platformSettings.locked_natural_profile_attributes])]
}

export function getShowPostalAddressFields() {
    return window.showPostalAddressFields
}

export function isNaturalProfileAttributeLocked(attribute) {
    const lockableNaturalProfileAttributes = getLockableNaturalProfileAttributes()

    return (
        (getAttributesLockedByPlatformSetting().includes(attribute) && usePersonalAccountDataState().isVerified) ||
        (isEsacPersonalComplianceProfileLocked() && lockableNaturalProfileAttributes.includes(attribute))
    )
}

export function isNaturalComplianceProfileLocked() {
    return (
        getPlatformSettings().compliance_profile === 'esac' &&
        isEsacPersonalComplianceProfileLocked() &&
        getLockableNaturalProfileRelations().includes('compliance_profiles')
    )
}

export function isNaturalTaxResidenciesEditDisabled() {
    return (
        getPlatformSettings().compliance_profile === 'esac' &&
        isEsacPersonalComplianceProfileLocked() &&
        getLockableNaturalProfileRelations().includes('tax_residencies')
    )
}
